import {
  keepPreviousData,
  useMutation,
  UseMutationOptions,
  useQuery,
} from "@tanstack/react-query"
import { AxiosError } from "axios"
import { useTranslation } from "react-i18next"

import { CountryIso3166, LibraryResponses } from "@tfg/types/endpoints/library"
import {
  orderByValidationType,
  riskQueryValidationType,
  SupplierGetQueryValidationType,
  supplierMonitoringFiltersValidationType,
  supplierRiskFiltersValidationType,
  transferContactValidationType,
} from "@tfg/types/schemas/organization/schemas"
import { fetchDataOptionsParam } from "@tfg/types/schemas/products"

import { httpClient } from "~/backend/axios.ts"
import { queryClient } from "~/backend/queryClient.ts"
import { showAxiosError } from "~/utils/showAxiosError"

export const useListSuppliersQuery = ({
  fetchDataOptions,
  searchSupplier,
  filters,
  orderBy,
}: Omit<SupplierGetQueryValidationType, "filters" | "orderBy"> &
  fetchDataOptionsParam & {
    filters?: Partial<supplierMonitoringFiltersValidationType>
    orderBy?: Partial<orderByValidationType>
  }) => {
  return useQuery<LibraryResponses.SuppliersResponse>({
    queryKey: ["suppliers", fetchDataOptions, searchSupplier, filters, orderBy],

    queryFn: async () => {
      const res = await httpClient.get<LibraryResponses.SuppliersResponse>(
        "suppliers/monitoring",
        {
          params: {
            ...fetchDataOptions,
            searchSupplier,
            filters:
              filters && Object.keys(filters).length > 0
                ? JSON.stringify(filters)
                : undefined,
            orderBy:
              orderBy && Object.keys(orderBy).length > 0
                ? JSON.stringify(orderBy)
                : undefined,
          },
        }
      )
      return res.data
    },

    placeholderData: keepPreviousData,
  })
}

export const useListSuppliersCountriesQuery = () => {
  return useQuery<CountryIso3166[]>({
    queryKey: ["suppliersCountries"],

    queryFn: async () => {
      const res =
        await httpClient.get<LibraryResponses.SuppliersCountriesResponse>(
          "traceability/suppliers/countries"
        )
      return res.data?.countries || []
    },
  })
}

export const useListSuppliersRiskQuery = ({
  fetchDataOptions,
  searchSupplier,
  filters,
}: Omit<riskQueryValidationType, "filters"> &
  fetchDataOptionsParam & {
    filters?: Partial<supplierRiskFiltersValidationType>
  }) => {
  return useQuery<LibraryResponses.SupplierRiskResponse>({
    queryKey: ["suppliers", fetchDataOptions, searchSupplier, filters],

    queryFn: async () => {
      const skip = fetchDataOptions.skip
      const take = fetchDataOptions.take
      const res = await httpClient.get<LibraryResponses.SupplierRiskResponse>(
        "suppliers/risk",
        {
          params: {
            skip,
            take,
            searchSupplier,
            filters:
              filters && Object.keys(filters).length > 0
                ? JSON.stringify(filters)
                : undefined,
          },
        }
      )
      return res.data
    },

    placeholderData: keepPreviousData,
  })
}
export const useTransferSupplierContactMutation = (
  options?: UseMutationOptions<
    LibraryResponses.Count,
    AxiosError,
    transferContactValidationType
  >
) => {
  return useMutation({
    ...options,
    mutationFn: async (req) => {
      return (await httpClient.post("suppliers/transfercontact", req)).data
    },
    onSuccess: async (data, variables, onMutateResult, context) => {
      await queryClient.invalidateQueries({ queryKey: ["suppliers"] })
      await queryClient.invalidateQueries({ queryKey: ["suppliersList"] })
      await queryClient.invalidateQueries({ queryKey: ["contactsList"] })
      await queryClient.invalidateQueries({ queryKey: ["listProductionSites"] })
      options?.onSuccess?.(data, variables, onMutateResult, context)
    },
  })
}

export const useTransferSupplierContactCertificatesMutation = (
  options?: UseMutationOptions<
    LibraryResponses.Count,
    AxiosError,
    transferContactValidationType
  >
) => {
  const { t } = useTranslation("traceability")
  return useMutation({
    ...options,
    mutationFn: async (req) => {
      return (
        await httpClient.post("suppliers/transfercontact/certificates", req)
      ).data
    },
    onSuccess: async (data, variables, onMutateResult, context) => {
      await queryClient.invalidateQueries({ queryKey: ["suppliers"] })
      await queryClient.invalidateQueries({ queryKey: ["suppliersList"] })
      await queryClient.invalidateQueries({ queryKey: ["contactsList"] })
      await queryClient.invalidateQueries({ queryKey: ["listProductionSites"] })
      options?.onSuccess?.(data, variables, onMutateResult, context)
    },
    onError: (error, variables, onMutateResult, context) => {
      options?.onError?.(error, variables, onMutateResult, context)
      showAxiosError({
        notificationTitle: t("errors.title"),
        notificationMessage: t("errors.axiosMessage", {
          message: error.response?.data,
        }),
        showNotification: true,
        error,
      })
    },
  })
}
